import { modelHelperModule } from './model-helper';
import { challengeItemModelModule } from './challenge-item';
import { quizQuestionModelModule } from './quiz-question';
import { codeFileModelModule } from './code-file';
import { languages } from '../constants/languages';
import { compactFalsy, empty, unique } from '../utils/array-utils';
import { lcFirst, ucFirst } from '../utils/string-utils';
import { getTeam, getUser } from '../utils/account-utils/account-utils';
import { defineValue, lazyProperty } from '../utils/object-utils/object-utils';
import { isObject } from '../utils/type-utils';
import { setClassicFilePaths } from '../services/codemirror-workspace-service/codemirror-workspace-service';

export const GLOBAL_COLLECTION = 'commons';
export const TEAM_COLLECTION = 'team';

export const INCLUDE_SOLUTIONS_REGEX =
  /(\s*(?:\/\/|#|--|;;|%%)\s*)@include-solution-from ([0-9a-fA-F]{24})[^\n]*/;
export const INCLUDE_SOLUTIONS_REGEX_GLOBAL = new RegExp(
  INCLUDE_SOLUTIONS_REGEX,
  'g',
);

export const CODE_CHALLENGE_TYPE = 'CodeChallenge';
export const ADVANCED_CODE_CHALLENGE_TYPE = 'AdvancedCodeChallenge';
export const QUIZ_CHALLENGE_TYPE = 'QuizChallenge';
export const TYPE_KEYS = {
  [CODE_CHALLENGE_TYPE]: 'code',
  [ADVANCED_CODE_CHALLENGE_TYPE]: 'advanced-code',
  [QUIZ_CHALLENGE_TYPE]: 'quiz',
};
export const TYPE_NAMES = {
  [CODE_CHALLENGE_TYPE]: 'Code',
  [ADVANCED_CODE_CHALLENGE_TYPE]: 'Project Code',
  [QUIZ_CHALLENGE_TYPE]: 'Q&A',
};
export const TYPE_TOOLTIPS = {
  [CODE_CHALLENGE_TYPE]:
    'A simplified coding based challenge, designed around a single editor for the solution',
  [ADVANCED_CODE_CHALLENGE_TYPE]:
    'An advanced coding based challenge, with multiple file support and additional features',
  [QUIZ_CHALLENGE_TYPE]:
    'A quiz-like challenge with questions and answers, not necessarily code-related',
};

export const UPDATE_VERSION = {
  MAJOR: 'major',
  MINOR: 'minor',
  PATCH: 'patch',
};

export const LIBRARY_ACCESS = ['limited', 'standard', 'extended'];

export const CHALLENGE_INITIAL_LAYOUT_DEFAULT = {
  instructions: 'topRight:1:1',
  webpreview: 'topRight:2',
  candidatenotes: 'topRight:3',
  help: 'topRight:4',
};

const DEFAULT_ESTIMATED_TIME = 5;

export const challengeModelModule = angular
  .module('Q.models.challenge', [
    modelHelperModule,
    challengeItemModelModule,
    quizQuestionModelModule,
    codeFileModelModule,
  ])
  .run(function ($rootScope) {
    const unlisten = $rootScope.$on('app.login', (event, { account } = {}) => {
      if (account && account.user && account.user.team) {
        if (account.user.team.hasFeature('advancedCodeChallenges')) {
          // if you have access to project challenges, we rename classic challenges
          TYPE_NAMES[CODE_CHALLENGE_TYPE] = 'Classic Code';
        }
        unlisten();
      }
    });
  })
  .factory(
    'Challenge',
    function (
      DS,
      ModelHelper,
      $q,
      $rootScope,
      $injector,
      $log,
      AdvancedCodeChallengeFile,
      AdvancedCodeChallengeReferenceFile,
    ) {
      const difficultiesList = [
        {
          id: 1,
          label: 'Basic',
          filter: { '<=': 1 }, // includes any value lower than 0 for filtering
        },
        {
          id: 2,
          label: 'Intermediate',
          filter: 2,
        },
        {
          id: 3,
          label: 'Advanced',
          filter: { '>=': 3 }, // Include Advanced and up for filtering
        },
        {
          id: 4,
          label: 'Advanced (Brutal)',
          filter: { '>=': 3 }, // Include Advanced and up for filtering
          hidden: true, // hide for filters, meters, etc
        },
      ];

      const difficulties = {};
      difficultiesList.forEach(({ id, label }) => {
        difficulties[id] = label;
      });

      const stateNameMappings = {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived',
      };

      const contentTypeMappings = {
        normal: 'Ready To Use',
        template: 'Template',
        tutorial: 'Tutorials',
      };

      const afterInject = (instance) => {
        delete instance.$languages;
        delete instance.$smartIdeAvailable;
        if (
          instance.$type === ADVANCED_CODE_CHALLENGE_TYPE &&
          !instance.initialLayout
        ) {
          instance.initialLayout = angular.copy(
            CHALLENGE_INITIAL_LAYOUT_DEFAULT,
          );
        }
      };

      /**
       * Returns the correct team to be looking at for feature checks
       * If the current user has a team (i.e., is a TeamMember), then we use that. Otherwise we use the team
       * associated with this instance (if available)
       * @param {Challenge} instance
       * @returns {Team|null}
       */
      const getFeatureTeam = (instance) => {
        return getTeam($injector) || instance.team;
      };

      const shouldFind = () => {
        const user = getUser($injector);
        return user && user.$type !== 'Candidate';
      };

      const Challenge = ModelHelper.defineResource({
        name: 'Challenge',
        endpoint: 'challenges',
        afterInject(resource, instance) {
          ModelHelper.afterInject(resource, instance);
          // after we inject or update a solution, we should always remove any cached revisions,
          // because they will never be updated
          if (Array.isArray(instance)) {
            instance.forEach(afterInject);
          } else {
            afterInject(instance);
          }
        },
        relations: {
          hasMany: {
            QuizQuestion: {
              $type: 'QuizChallenge',
              localField: 'questions',
              link: false,
            },
            AdvancedCodeChallengeFile: {
              $type: 'AdvancedCodeChallenge',
              localField: 'files',
              link: false,
            },
            AdvancedCodeChallengeReferenceFile: {
              $type: 'AdvancedCodeChallenge',
              localField: 'referenceFiles',
              link: false,
            },
          },
          hasOne: {
            ChallengeItem: {
              foreignKey: 'challengeId',
              foreignField: 'challenge',
            },
            Challenge: [
              {
                localField: 'sourcedFrom',
                localKey: 'sourcedFromId',
              },
              {
                localField: 'copiedFrom',
                localKey: 'copiedFromId',
              },
            ],
          },
          belongsTo: {
            Team: {
              localField: 'team',
              localKey: 'teamId',
            },
            TeamMember: [
              {
                localField: 'createdBy',
                localKey: 'createdById',
              },
            ],
          },
        },
        computed: {
          selectTitle: /* @ngInject */ function (title, demoData) {
            return title + (demoData ? ' (DEMO)' : '');
          },
          icon: {
            get() {
              if (!this.galleryImage) {
                if (
                  this.isClassicCode() &&
                  this.languages.length === 1 &&
                  languages[this.languages[0].name]
                ) {
                  return `lang-${languages[this.languages[0].name].id}`;
                } else if (
                  this.isAdvancedCode() &&
                  this.runConfig &&
                  languages[this.runConfig.language]
                ) {
                  return `lang-${languages[this.runConfig.language].id}`;
                } else if (this.isQuiz()) {
                  return `quiz`;
                }
              }
            },
          },
          // Returns topics which may be filtered due to similar signal subjects being shown
          signalTopics: {
            get() {
              const topics = this.topics;
              const subjects = compactFalsy(
                (this.getSubjects() || []).map((s) => s.label),
              );
              if (empty(topics) || empty(subjects)) return topics;

              // filter out any topics which are redundant with subjects
              return topics.filter(
                (topic) => !subjects.find((s) => s.includes(topic)),
              );
            },
          },
        },
        methods: {
          _runConfig() {
            return this.runConfig || {};
          },
          isGlobal() {
            return this.collection === GLOBAL_COLLECTION;
          },
          isLinked() {
            return !this.isGlobal() && this.linkedStatus;
          },
          isTeam() {
            return this.collection === TEAM_COLLECTION;
          },
          difficultyName() {
            return difficulties[this.difficulty];
          },
          stateName() {
            return this.isGlobal()
              ? contentTypeMappings[this.contentType]
              : stateNameMappings[this.state];
          },
          typeKey() {
            return TYPE_KEYS[this.$type] || TYPE_KEYS[CODE_CHALLENGE_TYPE];
          },
          typeName() {
            return TYPE_NAMES[this.$type] || TYPE_NAMES[CODE_CHALLENGE_TYPE];
          },
          typeTooltip() {
            return (
              TYPE_TOOLTIPS[this.$type] || TYPE_TOOLTIPS[CODE_CHALLENGE_TYPE]
            );
          },
          typeState() {
            return lcFirst(this.$type || CODE_CHALLENGE_TYPE);
          },
          typeSuffix() {
            return this.isQuestionTimed() ? ' with question time limits' : '';
          },
          isCode() {
            return this.isClassicCode() || this.isAdvancedCode();
          },
          isClassicCode() {
            return this.$type === CODE_CHALLENGE_TYPE;
          },
          isAdvancedCode() {
            return this.$type === ADVANCED_CODE_CHALLENGE_TYPE;
          },
          isQuiz() {
            return this.$type === QUIZ_CHALLENGE_TYPE;
          },
          webPreviews() {
            return this._runConfig().webPreviews;
          },
          versionsArray() {
            return [this.majorVersion, this.minorVersion, this.patchVersion];
          },
          hasVersion() {
            const versions = this.versionsArray();
            return (
              versions.filter(angular.isNumber).length === versions.length &&
              versions.some((v) => v > 0)
            );
          },
          fullVersion() {
            return this.versionsArray().join('.');
          },
          scoringSummary() {
            if (this.isQuiz()) {
              const questions = this.questions || [];
              const points = questions.reduce(
                (s, q) => s + (angular.isNumber(+q.weight) ? q.weight : 1),
                0,
              );
              let summary =
                questions.length +
                ' question' +
                (questions.length === 1 ? '' : 's');
              if (points === 0) {
                summary += ' (none scored)';
              } else {
                summary +=
                  ' worth ' + points + ' point' + (points === 1 ? '' : 's');
              }
              return summary;
            }
            return '';
          },
          canEdit() {
            return (
              this.isTeam() &&
              !this.protected &&
              $rootScope.permitted({ privilege: 'manageChallenges' })
            );
          },
          canPublish() {
            return this.canEdit() && Challenge.canPublish(this);
          },
          trackProperties() {
            return {
              title: this.title,
              id: this.id,
              teamId: this.teamId,
              state: this.state,
              summary: this.summary,
              copiedById: this.copiedById,
              createdById: this.createdById,
              difficulty: this.difficulty,
            };
          },
          loadSourcedFrom() {
            return Challenge.loadSourcedFrom([this]).then(() => this);
          },
          globalChallenge() {
            if (!this.isGlobal() && this.sourcedFrom) {
              const cf = this.sourcedFrom;
              if (cf.contentType === 'normal' && cf.isGlobal()) {
                return cf;
              }
            }
            return null;
          },
          hasStats(type, language) {
            const stats = this.stats(type, language);
            const globalStats = this.globalStats(type, language);
            // ignore if only one result
            return (
              !!(stats && stats.count > 1) ||
              !!(globalStats && globalStats.count > 1)
            );
          },
          stats(type, language) {
            const name = type + 'Stats';
            const challengeLanguage =
              language && this.language && this.language(language);
            return (challengeLanguage && challengeLanguage[name]) || this[name];
          },
          globalStats(type, language) {
            const gc = this.globalChallenge();
            return gc && gc.stats(type, language);
          },
          hasPremiumAccess() {
            return this.libraryAccess && this.libraryAccess !== 'limited';
          },
          hasChallengeTimeLimit(val) {
            if (val === undefined) {
              // we're specifically only tracking null here so you can safely edit the number
              return this.timeLimitMin !== null;
            } else {
              this.timeLimitMin = val ? 60 : null;
              // scored notes are only locked by a time limit, so don't leave the flag set without one
              if (!val) this.candidateNotesScored = false;
            }
          },
          // Placeholder for the candidate Your Notes tab: author text, else the default
          candidateNotesPlaceholder(noun = 'solution') {
            const custom = (this.candidateNotesTemplate || '').trim();
            return (
              custom ||
              `Add notes about your ${noun} to help explain your process.\n\nThis is markdown-enabled, so feel free to include code snippets, lists, etc.`
            );
          },
          // For quizzes, rely on timeLimitType, otherwise use timeLimitMin
          isUntimed() {
            return this.timeLimitType
              ? this.timeLimitType === 'none'
              : !this.isTimed();
          },
          // For quizzes, rely on timeLimitType, otherwise use timeLimitMin
          isTimed() {
            return this.timeLimitType
              ? this.timeLimitType === 'challenge'
              : this.timeLimitMin > 0;
          },
          isQuestionTimed() {
            return /^per_question/.test(this.timeLimitType);
          },
          isBonusTimed() {
            return this.timeLimitType === 'per_question_bonus';
          },
          totalTimeLimitMin() {
            if (this.isQuestionTimed()) {
              const timeLimitSec = (this.questions || [])
                .map((q) => q._timeLimitSec(this))
                .reduce((sum, sec) => sum + sec, 0);
              return Math.round(timeLimitSec / 60) || Infinity;
            } else {
              return this.isTimed() ? this.timeLimitMin : Infinity;
            }
          },
          timeLimitTypeLabel() {
            return Challenge.getEnum(
              'timeLimitType',
              this.timeLimitType || 'none',
            );
          },
          expectedTime() {
            const timeLimit = this.totalTimeLimitMin();
            const estimatedTime = this.estimatedTime;
            let time = DEFAULT_ESTIMATED_TIME;
            if (estimatedTime) {
              time = Math.min(estimatedTime, timeLimit);
            } else if (timeLimit < Infinity) {
              time = timeLimit;
            }
            return time;
          },
          hasLimitedSubmissionAttempts(val) {
            if (val === undefined) {
              // we're specifically only tracking null here so you can safely edit the number
              return this.limitSubmissionAttempts !== null;
            } else {
              this.limitSubmissionAttempts = val ? 3 : null;
            }
          },
          // matches assessment-template function for template consistency
          upgradeRequired() {
            return this.requiresUpgrade;
          },
          requiresUpgradeLabel() {
            const team = getTeam($injector);
            if (
              this.isAdvancedCode() &&
              (!team || !team.hasFeature('advancedCodeChallenges'))
            ) {
              return 'Project Code Challenges';
            }
            return `${ucFirst(this.libraryAccess)} Library`;
          },
          premiumFeature() {
            const team = getTeam($injector);
            if (
              this.isAdvancedCode() &&
              (!team || !team.hasFeature('advancedCodeChallenges'))
            ) {
              return 'projectCodeChallenges';
            }
            return `${this.libraryAccess}ChallengeLibrary`;
          },
          getLanguages(assessmentResult) {
            if (!this.$languages) {
              this.$languages = this._getLanguages(assessmentResult).filter(
                angular.identity,
              );
            }
            return this.$languages;
          },
          getSubjects() {
            return this.signalMappings
              ? this.signalMappings.subjects
              : (this.digest && this.digest.subjects) || [];
          },
          externalIdeEnabled() {
            const team = getFeatureTeam(this);
            return (
              team &&
              team.hasFeature('externalIde') &&
              this.isAdvancedCode() &&
              (this.enableExternalIde === 'enabled' ||
                (this.enableExternalIde === 'default' &&
                  team.defaultEnableExternalIde))
            );
          },
          codeOfConductConfig() {
            return {
              enabled: !!this.cocEnabled,
              aiUsageAllowed: this.cocAiUsageAllowed === true,
              externalIdeAllowed: this.cocExternalIdeAllowed === true,
              requireAcknowledgement: this.cocRequireAcknowledgement !== false,
            };
          },
          codeOfConductRuleMessage(type, allowed) {
            const messages = {
              aiUsage: {
                true: 'You may use external AI tools/assistance to help solve this challenge. We encourage you to leverage AI with your own thinking to create the best work possible.',
                false:
                  'You may not use AI tools/assistance while solving this challenge. We want to see your own work, not AI-generated work. Using AI will result in a failed assessment.',
              },
              externalIde: {
                true: '',
                false:
                  'You must perform your work within this browser. Using an external IDE/tool to develop your work will result in a failed assessment.',
              },
            };
            return messages[type] && messages[type][allowed ? 'true' : 'false'];
          },
          codeOfConductRuleAcknowledgementLabel(type, allowed) {
            const labels = {
              aiUsage: {
                true: 'I understand that I am allowed to use AI',
                false: 'I agree to not use AI',
              },
              externalIde: {
                true: '',
                false: 'I agree to work only in this browser',
              },
            };
            return labels[type] && labels[type][allowed ? 'true' : 'false'];
          },
          codeOfConductRules() {
            const config = this.codeOfConductConfig();
            const rules = [
              {
                id: 'aiUsage',
                allowed: config.aiUsageAllowed,
              },
            ];
            // External IDE policy is only relevant when AI usage is disallowed.
            if (!config.aiUsageAllowed) {
              rules.push({
                id: 'externalIde',
                allowed: config.externalIdeAllowed,
              });
            }
            return rules
              .map((rule) => {
                rule.message = this.codeOfConductRuleMessage(
                  rule.id,
                  rule.allowed,
                );
                rule.acknowledgementLabel =
                  this.codeOfConductRuleAcknowledgementLabel(
                    rule.id,
                    rule.allowed,
                  );
                return rule;
              })
              .filter(
                (rule) =>
                  rule.message !== null &&
                  rule.message !== undefined &&
                  String(rule.message).trim().length > 0,
              );
          },
          /**
           * Determines if SmartIDE is available on this challenge at all.
           *
           * @param {*} opts - Optional values
           * @param {boolean?} opts.overrideChallenge - If true, ignore the challenge's internal value
           * @param {boolean?} opts.enabledCheck - Private, true if we're checking strictly for currently enabled
           * @returns {boolean|string} - `true` if enabled for all languages, `'partial'` if enabled on some languages, `false` otherwise
           */
          smartIdeAvailable(opts = {}) {
            if (!this.$smartIdeAvailable) {
              this.$smartIdeAvailable = {};
            }
            const { enabledCheck, overrideChallenge } = opts;
            const language = enabledCheck ? this._runConfig().language : '';
            const key = `${language}|${overrideChallenge}`;

            if (!this.$smartIdeAvailable[key]) {
              const user = getUser($injector);
              const team = getFeatureTeam(this);
              let hasLanguageServers = false;
              if (enabledCheck || this.isAdvancedCode()) {
                hasLanguageServers =
                  (
                    this._runConfig().languageServers ||
                    this.languageServers ||
                    []
                  ).length > 0;
              } else if (this.isClassicCode()) {
                const langs = this.languages.length;
                const smartIdeLangs = this.languages.filter(
                  (l) => l.languageServers && l.languageServers.length > 0,
                ).length;
                if (smartIdeLangs === langs) {
                  hasLanguageServers = true;
                } else if (smartIdeLangs > 0) {
                  hasLanguageServers = 'partial';
                }
              }

              if (!user || !team || !hasLanguageServers) {
                this.$smartIdeAvailable[key] = false;
              } else {
                const featureEnabled = team.hasFeature('smartIde');

                const enabledOnChallenge = this.enableSmartIde === 'enabled';
                const enabledByDefault =
                  this.enableSmartIde === 'default' &&
                  team.defaultEnableSmartIde;
                const enabledByOverride =
                  overrideChallenge && team.defaultEnableSmartIde;

                this.$smartIdeAvailable[key] =
                  featureEnabled &&
                  (enabledOnChallenge ||
                    enabledByDefault ||
                    enabledByOverride) &&
                  hasLanguageServers;
              }
            }
            return this.$smartIdeAvailable[key];
          },
          /**
           * Determines if smartIDE is currently enabled on this challenge
           * @param {boolean} overrideChallenge - If true, ignore the challenge's internal value
           * @returns {boolean} - `true` if enabled, `false` otherwise
           */
          smartIdeEnabled(overrideChallenge) {
            return !!this.smartIdeAvailable({
              enabledCheck: true,
              overrideChallenge,
            });
          },
          /**
           * Creates a function to determine if a path is ignored
           * @returns {Function<String>} Function that takes a string and compares it against the ignored
           * paths. Returns `true` if ignored, false otherwise;
           */
          createIgnoreCheckFunction() {
            const paths = (
              this._runConfig().submissionIgnorePaths || []
            ).filter(angular.identity);
            if (paths.length) {
              const pathRegexes = paths.map((r) => new RegExp(r));
              return (path) => pathRegexes.some((r) => r.test(path));
            } else {
              return () => false;
            }
          },
          lockedLayout() {
            return (
              this.isClassicCode() ||
              (this.initialLayout && this.initialLayout.locked)
            );
          },
          responsiveMode() {
            return this.initialLayout && this.initialLayout.responsive;
          },
          canCloseTabs() {
            return !this.lockedLayout();
          },
        },
        staticMethods: {
          GLOBAL_COLLECTION,
          TEAM_COLLECTION,
          UPDATE_VERSION,
          collections: [
            { id: TEAM_COLLECTION, label: 'Team Library', shortLabel: 'Team' },
            {
              id: GLOBAL_COLLECTION,
              label: 'Qualified Library',
              shortLabel: 'Qualified',
            },
          ],

          loadSourcedFrom(challenges) {
            const ids = [];

            challenges.forEach((c) => {
              if (!c.$sourcedFromLoaded && !c.sourcedFrom && c.sourcedFromId) {
                ids.push(c.sourcedFromId);
                // only try once per challenge
                c.$sourcedFromLoaded = true;
              }
            });
            return ids.length && shouldFind()
              ? Challenge.findAll({ where: { id: { in: ids } } }).then(
                  () => challenges,
                )
              : $q.resolve(challenges);
          },

          getTypesList(suffix = '') {
            const list = [
              {
                id: CODE_CHALLENGE_TYPE,
                label: TYPE_NAMES[CODE_CHALLENGE_TYPE] + suffix,
                c: TYPE_KEYS[CODE_CHALLENGE_TYPE],
              },
              {
                id: QUIZ_CHALLENGE_TYPE,
                label: TYPE_NAMES[QUIZ_CHALLENGE_TYPE] + suffix,
                c: TYPE_KEYS[QUIZ_CHALLENGE_TYPE],
              },
            ];
            if (
              $rootScope.permitted &&
              $rootScope.permitted({ feature: 'advancedCodeChallenges' })
            ) {
              list.splice(0, 0, {
                id: ADVANCED_CODE_CHALLENGE_TYPE,
                label: TYPE_NAMES[ADVANCED_CODE_CHALLENGE_TYPE] + suffix,
                c: TYPE_KEYS[ADVANCED_CODE_CHALLENGE_TYPE],
              });
            }
            return list;
          },

          difficulties,
          difficultiesList,
          difficultiesVisibleList: difficultiesList.filter((v) => !v.hidden),

          LIBRARY_ACCESS,
          premiumAccess(accessLevel) {
            let accessIndex = LIBRARY_ACCESS.indexOf(accessLevel);
            if (accessIndex < 0) accessIndex = 0;
            return LIBRARY_ACCESS.slice(accessIndex + 1);
          },
          showAccessTags() {
            return (
              $rootScope.permitted &&
              $rootScope.permitted({ staffContent: true })
            );
          },

          INCLUDE_SOLUTIONS_REGEX,
          INCLUDE_SOLUTIONS_REGEX_GLOBAL,

          isCarriedForward(challenge, assessment) {
            return assessment.challengeItems.some((ci) => {
              return Challenge.getCarriedForwardIds(ci.challenge || {}).some(
                (cid) => challenge.id === cid,
              );
            });
          },
          hasBeenCarriedForward(challenge, assessmentResult) {
            return assessmentResult.validSolutions().some((sol) => {
              return (
                sol.challengeId !== challenge.id &&
                sol.challenge &&
                Challenge.getCarriedForwardIds(sol.challenge).some(
                  (cid) => cid === challenge.id,
                )
              );
            });
          },
          getCarriedForwardIds(challenge) {
            const ids = [];
            (challenge.languages || []).forEach((lang) => {
              const matches = (lang.setup || '').match(
                INCLUDE_SOLUTIONS_REGEX_GLOBAL,
              );
              if (matches) {
                matches.forEach((m) => {
                  const id = m.match(INCLUDE_SOLUTIONS_REGEX)[2];
                  if (ids.indexOf(id) === -1) {
                    ids.push(id);
                  }
                });
              }
            });
            return ids;
          },
          loadCarriedForward(challenge) {
            const ids = Challenge.getCarriedForwardIds(challenge).filter(
              (cid) => !!Challenge.get(cid),
            );
            if (ids.length && shouldFind()) {
              return Challenge.find({ where: { id: { in: ids } } }).then(
                () => true,
                () => false,
              );
            } else {
              return $q.resolve(true);
            }
          },
          replaceCarriedForward(language, code) {
            return (code || '').replace(
              INCLUDE_SOLUTIONS_REGEX_GLOBAL,
              (match, prefix, id) => {
                const c = Challenge.get(id);
                if (!c) {
                  return (
                    prefix +
                    '(Unable to find reference challenge for previous solution)'
                  );
                }

                const lang = c.language(language);
                if (lang && lang.solution) {
                  return (
                    prefix +
                    ' (Using reference solution from ' +
                    c.title +
                    ')\n' +
                    lang.solution
                  );
                } else {
                  return (
                    prefix +
                    ' (Reference solution not available for ' +
                    c.title +
                    ')'
                  );
                }
              },
            );
          },
          canPublish(challengeData) {
            let hasContent = true;
            const type = challengeData.$type;
            if (type === QUIZ_CHALLENGE_TYPE) {
              hasContent =
                challengeData.questions &&
                challengeData.questions.length &&
                challengeData.questions.every((q) => q && q.prompt);
            } else if (type === CODE_CHALLENGE_TYPE) {
              hasContent =
                challengeData.languages &&
                challengeData.languages.length &&
                challengeData.languages.every(
                  (l) => l && l.solution && l.testCases,
                );
            } else if (type === ADVANCED_CODE_CHALLENGE_TYPE) {
              hasContent = challengeData.files && challengeData.files.length;
            }
            return (
              hasContent && challengeData.title && challengeData.instructions
            );
          },
          getRunFiles(
            challenge,
            filesContainer = challenge,
            submission,
            reference,
          ) {
            const includeKey = submission ? 'includeOnSubmit' : 'includeOnTest';
            const baseFiles = filesContainer.files.filter(
              (f) => !f.directory && f[includeKey],
            );
            const ignored = challenge.createIgnoreCheckFunction();
            if (reference) {
              const refFiles = Array.isArray(reference)
                ? reference
                : filesContainer.referenceFiles;
              // add or override valid reference files
              refFiles
                .filter(
                  (f) => !f.directory && (!submission || !ignored(f.path)),
                )
                .forEach((rf) => {
                  const setupFileIndex = baseFiles.findIndex(
                    (f) => f.path === rf.path,
                  );
                  if (setupFileIndex > -1) {
                    if (baseFiles[setupFileIndex].$access === 'readwrite') {
                      baseFiles[setupFileIndex] = rf;
                    }
                  } else {
                    baseFiles.push(rf);
                  }
                });
            }
            return baseFiles.map((f) => ({
              path: f.path,
              contents: f.contents,
              get encrypted() {
                return f.encrypted;
              },
              get reference() {
                return f.reference;
              },
              get $access() {
                return f.$access || f.access;
              },
            }));
          },
        },
        meta: {
          hasDigest: true,
          types: {
            CodeChallenge: {
              afterTyped(instance) {
                // ensure languages is not missing
                instance.languages = instance.languages || [];

                // configure current language (if already configured, reconfigure it)
                instance.setCurrentLanguage(instance.$currentLanguage);

                // Mock files & referenceFiles arrays to simulate an AdvancedCodeChallenge
                const createFile = (field, reference = false) => {
                  // const testCases = /testcases/i.test(field);
                  const FileModel = reference
                    ? AdvancedCodeChallengeReferenceFile
                    : AdvancedCodeChallengeFile;
                  const file = FileModel.createInstance({
                    id: 'challenge$' + field.toLowerCase(),
                    field,
                    deletable: false,
                    access: 'readwrite',
                    isClassicCode: true,
                    $hideFromInteract: true,
                  });
                  Object.defineProperties(file, {
                    contents: {
                      get() {
                        return instance.$currentLanguage[field];
                      },
                      set(val) {
                        instance.$currentLanguage[field] = val;
                      },
                    },
                  });
                  return file;
                };

                const createFiles = (key, fields, reference) => {
                  lazyProperty(instance, key, () => {
                    const files = fields.map((f) => createFile(f, reference));

                    files.updatePaths = () => {
                      return setClassicFilePaths({
                        files,
                        language: instance.$currentLanguage.name,
                        testFramework: instance.$currentLanguage.testFramework,
                        languageVersion:
                          instance.$currentLanguage.languageVersion,
                        $log,
                      });
                    };
                    defineValue(instance, '_updatePaths', files.updatePaths);

                    files.updatePaths();
                    // TODO: do we ever need to track renamed files here?
                    instance.on('afterInject', files.updatePaths);

                    return files;
                  });
                };

                createFiles('files', [
                  'setup',
                  'preloaded',
                  'exampleTestCases',
                ]);
                createFiles('referenceFiles', ['solution', 'testCases'], true);
              },
              /**
               * Stores the current working language on the challenge to make it easier to worth with a
               * classic code challenge in multi-file components.
               *
               * @param {Object|string?} nameOrLanguage - Name of a language, or a language object to set
               */
              setCurrentLanguage(nameOrLanguage) {
                const challenge = this;
                const langName =
                  (isObject(nameOrLanguage) && nameOrLanguage.name) ||
                  nameOrLanguage;
                // always look up the actual language object
                const lang = challenge.language(langName) || {};
                defineValue(challenge, '$currentLanguage', lang);
                defineValue(challenge, 'runConfig', {
                  language: lang.name,
                  languageVersion: lang.languageVersion,
                  testFramework: lang.testFramework,
                  preset: null,
                  webPreviews: false,
                  services: challenge.enabledServices,
                  languageServers: lang.languageServers,
                });
                if (challenge._updatePaths) {
                  challenge._updatePaths();
                }
              },
              language(name) {
                return name
                  ? this.languages.find((language) => language.name === name)
                  : undefined;
              },
              _getLanguages(assessmentResult) {
                let langs = this.languages.map((l) =>
                  languages.getLang(l.name),
                );
                if (assessmentResult) {
                  const ci = assessmentResult.getChallengeItem(this);
                  langs = langs.filter(
                    (lang) => !ci.disabledLanguages.includes(lang.id),
                  );
                }
                return langs;
              },
            },
            AdvancedCodeChallenge: {
              getRunFiles(submission, reference, updates) {
                let filesContainer = this;
                if (updates) {
                  filesContainer = {
                    files: updates.files || this.files,
                    referenceFiles:
                      updates.referenceFiles || this.referenceFiles,
                  };
                }
                return Challenge.getRunFiles(
                  this,
                  filesContainer,
                  submission,
                  reference,
                );
              },
              _getLanguages() {
                const lang =
                  this._runConfig().language ||
                  (this.languages &&
                    this.languages[0] &&
                    this.languages[0].name);
                if (lang) {
                  return [languages[lang]];
                } else {
                  return [];
                }
              },
            },
            QuizChallenge: {
              _getLanguages() {
                const allLangs = this.questions
                  .map((q) => q.language)
                  .map(languages.getLang)
                  .filter(angular.identity);
                return unique(allLangs, (l) => l.id);
              },
            },
          },
          defaults: {
            initialLayout: CHALLENGE_INITIAL_LAYOUT_DEFAULT,
          },
          actionEndpoints: {
            import: { track: true },
            duplicate: { segment: 'import' },
            unpublish: { method: 'PUT', track: true },
            archive: { method: 'PUT', track: true },
            unarchive: { method: 'PUT', track: true },
            duplicateToShared: { method: 'PUT', track: true },
            export: { method: 'POST', track: true, cacheResponse: false },
          },
          track: {
            create: true,
            update: true,
          },
          includeOnPull: ['digest'],
          excludeSaveOrCreateProps: ['questions', 'files', 'referenceFiles'],
          afterSaveOrCreate(instance, changes) {
            let fields = [];
            if (instance.$type === 'AdvancedCodeChallenge') {
              fields = [
                {
                  Model: DS.definitions.AdvancedCodeChallengeFile,
                  fieldName: 'files',
                  foreignKey: 'challengeId',
                  changes,
                  orderable: false,
                  uniqueChildField: 'path',
                },
                {
                  Model: DS.definitions.AdvancedCodeChallengeReferenceFile,
                  fieldName: 'referenceFiles',
                  foreignKey: 'challengeId',
                  changes,
                  orderable: false,
                  uniqueChildField: 'path',
                },
              ];
            }
            if (instance.$type === 'QuizChallenge') {
              fields = {
                Model: DS.definitions.QuizQuestion,
                fieldName: 'questions',
                foreignKey: 'challengeId',
                changes,
              };
            }
            return ModelHelper.saveEmbedded(fields);
          },
        },
      });

      return Challenge;
    },
  );
